import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, IconButton } from '@mui/material';
import { Create, OptionsMenuImg } from '../../assets/icons';
import { ClassroomGrid } from './styles';
import { useNewspaperStore } from '../../hooks/zustand/newspapers';
import { useClassroomStore } from '../../hooks/zustand/classrooms';
import * as NewspapersAPI from '../../api/Newspapers';
import CreateNewspaperDialog from '../../components/dialogs/CreateNewspaperDialog';
import NewTeacherWizard from '../../components/dialogs/NewTeacherWizard';
import ContextMenu from '../../components/ContextMenu';
import { ContextMenuItemProps, ContextMenuRef } from '../../components/ContextMenu/types';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';

function NewspaperCard({ newspaper, onDeleted }: { newspaper: any; onDeleted: () => void }) {
  const navigate = useNavigate();
  const contextMenuRef = useRef<ContextMenuRef>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const menuItems = useMemo<ContextMenuItemProps[]>(() => [
    {
      label: 'Delete',
      icon: 'BIN',
      onClick: () => setIsDeleteOpen(true),
      style: { color: '#FF5252 !important' },
    },
  ], []);

  const handleDropdownClick = (e: any) => {
    e.stopPropagation();
    contextMenuRef.current?.openMenu(e);
  };

  const handleDelete = async () => {
    await NewspapersAPI.deleteNewspaper(newspaper.id);
    onDeleted();
  };

  return (
    <>
      <ContextMenu menuItems={menuItems} ref={contextMenuRef}>
        <Card
          className="pressto-button classroom-button"
          onClick={() => navigate(`/newspaper/${newspaper.id}`)}
          elevation={16}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            padding: '0 !important',
            background: '#faf7f0 !important',
            color: '#1a1a1a !important',
            border: '1px solid #d4c9a8',
            overflow: 'hidden',
            position: 'relative',
            '&:hover': { background: '#f5f0e3 !important', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
          }}
        >
          {/* Submitted stories badge */}
          {(newspaper.submitted_stories_count ?? 0) > 0 && (
            <div style={{
              position: 'absolute',
              top: -6,
              right: -6,
              background: '#e65100',
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 700,
              width: 22,
              height: 22,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}>
              {newspaper.submitted_stories_count}
            </div>
          )}
          {/* Three dots */}
          <IconButton
            className="pressto-button-actions"
            onClick={handleDropdownClick}
            sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
          >
            <img src={OptionsMenuImg} alt="options menu" />
          </IconButton>
          {/* Masthead */}
          <div style={{
            borderBottom: '3px double #1a1a1a',
            padding: '0.6rem 0.75rem 0.4rem',
            textAlign: 'center',
            width: '100%',
          }}>
            <div style={{
              fontFamily: '"Georgia", "Times New Roman", serif',
              fontWeight: 700,
              fontSize: '1.1rem',
              lineHeight: 1.2,
              letterSpacing: '0.02em',
            }}>
              {newspaper.name}
            </div>
            <div style={{
              fontSize: '0.55rem',
              opacity: 0.5,
              marginTop: '2px',
              fontFamily: '"Georgia", serif',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>
              {newspaper.scope_type} edition
            </div>
          </div>
          {/* Faux columns */}
          <div style={{
            flex: 1,
            display: 'flex',
            gap: '6px',
            padding: '0.5rem 0.75rem',
            width: '100%',
          }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
              <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '80%' }} />
              <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
              <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '60%' }} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '70%' }} />
              <div style={{ height: 4, background: '#ccc', borderRadius: 1 }} />
              <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '90%' }} />
              <div style={{ height: 4, background: '#ccc', borderRadius: 1, width: '50%' }} />
            </div>
          </div>
          {/* Footer */}
          <div style={{
            borderTop: '1px solid #d4c9a8',
            padding: '0.35rem 0.75rem',
            fontSize: '0.65rem',
            color: '#888',
            fontFamily: '"Georgia", serif',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}>
            <span>{newspaper.editions_count} {newspaper.editions_count === 1 ? 'edition' : 'editions'}</span>
            <span style={{ color: (newspaper.submitted_stories_count ?? 0) > 0 ? '#e65100' : undefined, fontWeight: (newspaper.submitted_stories_count ?? 0) > 0 ? 700 : undefined }}>
              {(newspaper.submitted_stories_count ?? 0) > 0
                ? `${newspaper.submitted_stories_count} turned in`
                : `${newspaper.students_count ?? 0} ${newspaper.students_count === 1 ? 'student' : 'students'}`}
            </span>
          </div>
        </Card>
      </ContextMenu>
      <DeleteConfirmationDialog
        title="Delete newspaper?"
        description={`Are you sure you want to delete "${newspaper.name}"? This will also delete all editions and stories.`}
        onSubmit={handleDelete}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
      />
    </>
  );
}

export default function NewspapersTab() {
  const newspapers = useNewspaperStore((state) => state.newspapers);
  const loadNewspapers = useNewspaperStore((state) => state.loadNewspapers);
  const classrooms = useClassroomStore((state) => state.classrooms);
  const loadClassrooms = useClassroomStore((state) => state.loadClassrooms);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  useEffect(() => {
    loadNewspapers();
    loadClassrooms();
  }, []);

  window.document.title = 'Newspapers';

  return (
    <div>
      <ClassroomGrid>
        <div className="pressto-count-row">
          <div style={{ marginTop: '.5rem' }} className="pressto-count">
            <strong>{newspapers.length}</strong>
            {' '}
            {newspapers.length === 1 ? 'newspaper' : 'newspapers'}
          </div>
        </div>
        <div className="content">
          <Card
            className="create pressto-button classroom-button"
            onClick={() => classrooms.length === 0 ? setIsWizardOpen(true) : setIsCreateDialogOpen(true)}
            elevation={16}
            sx={{ backgroundColor: 'unset' }}
          >
            <img src={Create} alt="+" />
            Start a newspaper
          </Card>
          {newspapers.map((newspaper) => (
            <NewspaperCard
              key={newspaper.id}
              newspaper={newspaper}
              onDeleted={loadNewspapers}
            />
          ))}
        </div>
      </ClassroomGrid>

      <CreateNewspaperDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />

      <NewTeacherWizard
        isOpen={isWizardOpen}
        onClose={() => {
          setIsWizardOpen(false);
          loadNewspapers();
          loadClassrooms();
        }}
      />
    </div>
  );
}
